import NiceModal, { NiceModalHandler, NiceModalHocProps, register, useModal } from "@ebay/nice-modal-react";
import { Dialog, DialogActions, DialogContent, DialogProps } from "@mui/material";
import { useCallback } from "react";
import { useTypedTranslation } from "../../hooks/useTypedTranslation";
import { InternalButtonLink } from "../InternalButtonLink";
import { DummyComponent } from "../MuiHelpers";
import * as StyledModals from "./Modals.styled";
import { Modals } from "./modals";

export type ModalConfirmCloseReasons = "buttonClick" | "backdropClick" | "escapeKeyDown";

// Adapter to satisfy ModalConfirm expected signature onClick?: () => void
const DialogActionButtonAdapter: React.FC<{
    onClick?: () => void;
    children?: React.ReactNode;
    component?: React.ElementType;
    variant?: any;
}> = ({ onClick, ...rest }) => (
    <InternalButtonLink
        {...rest}
        component="button"
        // Normalize to no-arg callback
        onClick={() => {
            onClick?.();
        }}
    />
);

export type ModalConfirmComponents = {
    PaperComponent: React.ElementType<
        React.PropsWithChildren<{
            width: number | string;
        }>
    >;
    ModalTitleComponent: React.ElementType<React.PropsWithChildren<{ strong?: boolean; handleClose?: () => void }>>;
    DialogContentWrapperComponent: React.ElementType<React.PropsWithChildren<{}>>;
    DialogActionsWrapperComponent: React.ElementType<React.PropsWithChildren<{}>>;
    DialogActionButtonComponent: React.ElementType<
        React.PropsWithChildren<{
            onClick?: () => void;
            component?: React.ElementType<any, keyof JSX.IntrinsicElements>;
        }>
    >;
    DialogComponent: React.ElementType<DialogProps>;
};

export const modalConfirmDefaultComponents: ModalConfirmComponents = {
    PaperComponent: StyledModals.ModalPaper,
    ModalTitleComponent: StyledModals.ModalDialogTitle,
    DialogContentWrapperComponent: DialogContent,
    DialogActionsWrapperComponent: DialogActions,
    DialogActionButtonComponent: DialogActionButtonAdapter,
    DialogComponent: Dialog,
} satisfies ModalConfirmComponents;

export type ModalConfirmProps<TContentProps = Record<string, unknown>> = {
    title: string;
    additionalContentProps?: TContentProps;
    content: React.ComponentType<{ parentModal?: NiceModalHandler<Record<string, unknown>> } & TContentProps>;
    buttonText?: string;
    width?: number | string;
    onCloseOverride?: ({
        preventDefault,
        reason,
        modal,
    }: {
        preventDefault: () => void;
        reason: ModalConfirmCloseReasons;
        modal: NiceModalHandler<Record<string, unknown>>;
    }) => void;
    components?: Partial<ModalConfirmComponents>;
};

// Generic component function
// eslint-disable-next-line prettier/prettier
const ModalConfirmComponent = <
       TContentProps = Record<string, unknown>,
    >({
    title,
    content,
    buttonText,
    width = 480,
    onCloseOverride,
    components,
    additionalContentProps,
}: ModalConfirmProps<TContentProps>) => {
    const modal = useModal();
    const { t } = useTypedTranslation();

    const {
        DialogComponent,
        PaperComponent,
        ModalTitleComponent,
        DialogContentWrapperComponent,
        DialogActionsWrapperComponent,
        DialogActionButtonComponent,
    }: ModalConfirmComponents = {
        ...modalConfirmDefaultComponents,
        ...components,
    };

    const handleClose = useCallback(
        (reason: ModalConfirmCloseReasons, result: boolean) => {
            let defaultPrevented = false;

            onCloseOverride?.({
                reason: reason,
                modal,
                preventDefault: () => {
                    defaultPrevented = true;
                },
            });

            if (!defaultPrevented) {
                modal.resolve(result);
                modal.hide();
            }
        },
        [modal, onCloseOverride],
    );

    const handleButton = useCallback(() => {
        handleClose("buttonClick", true);
    }, [handleClose]);

    const ContentComponent = content;

    return (
        <DialogComponent
            // we have to use a dummy component here because MUI Dialog requires a PaperComponent prop,
            // if notprovided it use Paper and it has a global style (see theme.js) that we don't want to use
            PaperComponent={DummyComponent}
            open={modal.visible}
            onClose={(_, reason) => handleClose(reason, false)}
        >
            <PaperComponent width={width}>
                <ModalTitleComponent strong>{title}</ModalTitleComponent>
                <DialogContentWrapperComponent>
                    <ContentComponent
                        parentModal={modal}
                        {...((additionalContentProps as TContentProps) || ({} as TContentProps))}
                    />
                </DialogContentWrapperComponent>
                <DialogActionsWrapperComponent>
                    <DialogActionButtonComponent component="button" onClick={handleButton}>
                        {buttonText ?? t("global.iUnderstand")}
                    </DialogActionButtonComponent>
                </DialogActionsWrapperComponent>
            </PaperComponent>
        </DialogComponent>
    );
};

type ModalConfirmGenericComponent = {
    <TContentProps = Record<string, unknown>>(
        modalProps: ModalConfirmProps<TContentProps> & NiceModalHocProps,
    ): JSX.Element;
};

// Export the generic modal with proper typing
export const ModalConfirm = NiceModal.create(ModalConfirmComponent) as ModalConfirmGenericComponent;

register(Modals.ModalConfirm, ModalConfirm);
